/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import {
  AnnotationSectionPages,
  type DocumentActe,
  type DocumentListe,
  SectionCanva,
  SectionListeDoc,
  type TiffPage,
  urlDocumentBinaire,
} from '@mairistem/administres-utils';

import { BASE_URL_ETATCIVIL } from '@/constants';
import { formatDateSeule } from '@/utils';
import type { FormatDocument } from '@/utils/documentPreview';

import ApercuChargement from './ApercuChargement';
import ApercuDocx from './ApercuDocx';
import ApercuIndisponible from './ApercuIndisponible';
import ApercuTexte from './ApercuTexte';
import VignettesDocx from './VignettesDocx';
import ModalAjouterDocument, {
  type NouveauDocument,
} from './ModalAjouterDocument';
import ModalModifierDocument, {
  type ModificationDocument,
} from './ModalModifierDocument';

interface ActeDocumentsListeProps {
  /** Documents du dossier, chargés par l'écran. */
  documents: Array<DocumentActe>;
  /** Document affiché. */
  selection?: number;
  onSelectionChange: (identifiant?: number) => void;
  /** Pages décodées du document affiché. */
  pages: Array<TiffPage>;
  format: FormatDocument;
  /** Octets du document, pour les formats rendus hors canevas. */
  binaire?: Uint8Array;
  /** Le document existe mais n'a aucun contenu (API en 404). */
  sansContenu?: boolean;
  chargement?: boolean;
  /** Page affichée, numérotée à partir de 1. */
  pageCourante: number;
  onPageChange: (numero: number) => void;
  /** Ajoute un document au dossier ; renvoie le document créé. */
  onAjouter: (
    document: NouveauDocument,
  ) => Promise<{ identifiant?: number } | null>;
  /** Enregistre le nom et la description modifiés. */
  onModifier: (identifiant: number, modification: ModificationDocument) => void;
  /** Rechargement de la liste, après une suppression. */
  onRefresh: () => void;
  /** Désactive l'ajout tant que l'acte n'est pas connu. */
  acteConnu?: boolean;
}

/**
 * Onglet « Liste des documents » de la fiche d'un acte numérisé.
 *
 * Trois colonnes, comme la maquette : les documents du dossier à gauche,
 * l'aperçu du document sélectionné au centre, ses pages à droite.
 *
 * Composant d'affichage : le chargement des documents et le décodage du binaire
 * appartiennent à l'écran, pour survivre au changement d'onglet.
 */
const ActeDocumentsListe = (props: ActeDocumentsListeProps) => {
  const {
    documents,
    selection,
    onSelectionChange,
    pages,
    format,
    binaire,
    sansContenu = false,
    chargement = false,
    pageCourante,
    onPageChange,
    onAjouter,
    onModifier,
    onRefresh,
    acteConnu = true,
  } = props;

  const [aModifier, setAModifier] = React.useState<number | undefined>();
  const [ajoutOuvert, setAjoutOuvert] = React.useState(false);
  const [ajoutEnCours, setAjoutEnCours] = React.useState(false);
  /**
   * Pages rendues d'un DOCX, remontées par l'aperçu.
   *
   * `docx-preview` rend le document dans le DOM sans produire d'images : les
   * vignettes sont des réductions de ces pages, pas des images décodées.
   */
  const [sectionsDocx, setSectionsDocx] = React.useState<Array<HTMLElement>>(
    [],
  );

  const documentsListe = React.useMemo(
    () =>
      _.map(documents, (doc) => ({
        identifiant: doc.identifiant,
        // La date de dépôt (`dc_date`) accompagne le nom : dans un dossier qui
        // en compte une vingtaine, elle est le seul repère chronologique.
        nom: _.compact([doc.nom, formatDateSeule(doc.date)]).join(' - '),
        nombrePages: 0,
        /*
          Pastille du format, pour repérer les TIFF — les seuls annotables.
          `extension` est normalisée par l'API : `dc_type` est saisi en casse
          libre et souvent vide. La chaîne vide est volontaire, elle rend une
          pastille neutre plutôt qu'un trou dans la colonne.
        */
        extension: doc.extension ?? '',
        // `dc_zsaisie`, faute de colonne de description dans `ec_documents`.
        // Rien n'est affiché quand elle est vide : un libellé générique
        // n'apprendrait rien de plus que la pastille de format.
        description: doc.zoneSaisie,
      })),
    [documents],
  );

  const documentAffiche = React.useMemo(
    () => _.find(documents, { identifiant: selection }),
    [documents, selection],
  );

  const documentAModifier = React.useMemo(
    () => _.find(documents, { identifiant: aModifier }),
    [documents, aModifier],
  );

  const handleModification = React.useCallback(
    (modification: ModificationDocument) => {
      if (aModifier) onModifier(aModifier, modification);
      setAModifier(undefined);
    },
    [aModifier, onModifier],
  );

  const handleAjout = React.useCallback(
    (nouveau: NouveauDocument) => {
      setAjoutEnCours(true);
      onAjouter(nouveau)
        .then((cree) => {
          if (!cree) return;
          setAjoutOuvert(false);
          toast.success(`Le document « ${nouveau.nom} » a bien été ajouté`);
          // Afficher tout de suite le document qu'on vient de déposer.
          if (cree.identifiant) onSelectionChange(cree.identifiant);
        })
        .finally(() => setAjoutEnCours(false));
    },
    [onAjouter, onSelectionChange],
  );

  /*
    Le format n'est connu qu'une fois le binaire décodé : avant, il vaut
    `inconnu`, ce qui n'est pas la même chose qu'un format non affichable.
    Annoncer « ce format ne peut pas être affiché » pendant le chargement, ou
    quand aucun document n'est sélectionné, serait un contresens.
  */
  const sansApercu =
    !_.isNil(documentAffiche) && (format === 'doc' || format === 'inconnu');

  const handleImpression = React.useCallback(() => {
    if (selection) {
      window.open(urlDocumentBinaire(selection), '_blank');
    }
  }, [selection]);

  return (
    /*
      Mise en page en flex, et non avec la `Grid` de Semantic : ses colonnes
      ajoutent un padding vertical qui faisait dépasser l'aperçu, et surtout
      elles ne sont pas des conteneurs flex — or `AnnotationSectionPages`
      s'étire par `flex: 1`, qui restait donc sans effet. Mêmes proportions que
      l'onglet d'annotation.
    */
    <div
      style={{
        display: 'flex',
        height: '100%',
        gap: '1em',
        padding: '1em',
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          flex: 4,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <SectionListeDoc
          documents={documentsListe}
          selectedDocumentId={selection}
          onDocumentClick={onSelectionChange}
          // `onAddDocumentRequest` court-circuite la modale intégrée de
          // `SectionListeDoc`, qui demande un type de document Hubée sans objet
          // ici. La nôtre demande le rattachement « document typé acte ».
          onAddDocumentRequest={() => setAjoutOuvert(true)}
          // Jamais appelé : la modale intégrée ne s'ouvre plus.
          onAddDocumentClick={_.noop}
          onDocumentDeleted={onRefresh}
          onDocumentEdit={(doc: DocumentListe) => setAModifier(doc.identifiant)}
          // `DeleteDocumentModal` concatène lui-même `/documents/{id}`.
          apiRoute={BASE_URL_ETATCIVIL}
          disabled={!acteConnu}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flex: 9,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/*
          Chaque format a son panneau : le canevas ne sait afficher que des
          images, le RTF donne du texte, le DOCX se rend dans le DOM, et le Word
          97-2003 ne se rend pas du tout.

          Le panneau d'attente passe avant tous les autres : le format du
          document en cours de chargement n'est pas encore connu, et laisser le
          panneau précédent en place afficherait le contenu du document qu'on
          vient de quitter.
        */}
        {chargement ? (
          <ApercuChargement />
        ) : format === 'rtf' || format === 'txt' ? (
          <ApercuTexte texte={pages[0]?.src} />
        ) : format === 'docx' ? (
          <ApercuDocx
            binaire={binaire}
            pageCourante={pageCourante}
            onSectionsChange={setSectionsDocx}
          />
        ) : sansApercu ? (
          <ApercuIndisponible
            binaire={binaire}
            nom={documentAffiche?.nom ?? 'document'}
            /*
              Un document vide et un format non affichable sont deux choses
              différentes : la reprise de données a laissé des documents sans
              aucun contenu, et « ce format ne peut pas être affiché » ne dit
              alors rien de vrai.
            */
            message={sansContenu ? "Ce document n'a aucun contenu" : undefined}
          />
        ) : (
          <SectionCanva
            images={pages}
            // `SectionCanva` raisonne en index, le reste de l'écran en numéro
            // de page.
            selectedIndex={pageCourante - 1}
            onImageChange={(index: number) => onPageChange(index + 1)}
            onPrint={handleImpression}
            fitMode="width"
            /*
              Pas de `height` ici : `SectionCanva` pose lui-même
              `calc(100% - 40px)` pour retirer son en-tête, et le `style` reçu
              en prop écrase ce calcul — le canevas dépassait alors de la
              hauteur exacte de l'en-tête.
            */
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flex: 3,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/*
          Le DOCX n'a pas d'images de pages : ses vignettes sont des
          réductions des pages rendues dans l'aperçu.
        */}
        {format === 'docx' ? (
          <VignettesDocx
            sections={sectionsDocx}
            pageCourante={pageCourante}
            onPageChange={onPageChange}
          />
        ) : (
          <AnnotationSectionPages
            /*
              `isTiff` commande la vignette : une image pour les formats
              décodés en images, le texte lui-même pour le RTF et le TXT.
            */
            isTiff={format !== 'rtf' && format !== 'txt'}
            pages={pages}
            selectedPage={pageCourante}
            onPageChange={onPageChange}
            title="PAGE(S) DU DOCUMENT"
          />
        )}
      </div>

      <ModalAjouterDocument
        open={ajoutOuvert}
        saving={ajoutEnCours}
        onClose={() => setAjoutOuvert(false)}
        onConfirm={handleAjout}
      />
      <ModalModifierDocument
        open={!_.isNil(aModifier)}
        nom={documentAModifier?.nom}
        description={documentAModifier?.zoneSaisie}
        onClose={() => setAModifier(undefined)}
        onConfirm={handleModification}
      />
    </div>
  );
};

export default ActeDocumentsListe;
